/**
 * Identité de l'utilisateur connecté, à disposition de toute l'application.
 *
 * Un contexte React suffit — pas de bibliothèque de gestion d'état : il y a une seule valeur, elle
 * change deux fois par session (connexion, déconnexion), et `23-DO-NOT.md` interdit d'ajouter une
 * bibliothèque structurante sans besoin démontré. TanStack Query s'occupe de l'état **serveur**, qui
 * est le seul état non trivial de cette application.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { definirFournisseurDeJeton } from "../api/client";
import {
  identiteCourante,
  jetonCourant,
  seConnecter as seConnecterCognito,
  seDeconnecter as seDeconnecterCognito,
  type Identite,
} from "./cognito";

type ContexteAuth = {
  identite: Identite | null;
  /** `true` le temps de savoir si une session valide existe déjà, au premier rendu. */
  chargement: boolean;
  seConnecter: (email: string, motDePasse: string) => Promise<void>;
  seDeconnecter: () => void;
  /** Remplacée par les tests pour injecter une identité sans passer par Cognito. */
  definirIdentite: (identite: Identite | null) => void;
};

const Contexte = createContext<ContexteAuth | null>(null);

export function FournisseurAuth({
  children,
  identiteInitiale = null,
  restaurerLaSession = true,
}: {
  children: React.ReactNode;
  identiteInitiale?: Identite | null;
  restaurerLaSession?: boolean;
}) {
  const [identite, definirIdentite] = useState<Identite | null>(identiteInitiale);
  const [chargement, definirChargement] = useState(restaurerLaSession);

  // Le client d'API ne connaît pas Cognito : il lui demande un jeton par cette fonction. Le
  // rafraîchissement éventuel est géré par la bibliothèque, à chaque appel.
  useEffect(() => {
    definirFournisseurDeJeton(jetonCourant);
  }, []);

  useEffect(() => {
    if (!restaurerLaSession) return;
    let abandonne = false;
    identiteCourante()
      .then((trouvee) => {
        if (!abandonne) definirIdentite(trouvee);
      })
      .catch(() => {
        // Une session illisible ou expirée équivaut à pas de session : l'écran de connexion
        // s'affiche, sans message d'erreur technique qui n'apprendrait rien à l'utilisateur.
      })
      .finally(() => {
        if (!abandonne) definirChargement(false);
      });
    return () => {
      abandonne = true;
    };
  }, [restaurerLaSession]);

  const connecter = useCallback(async (email: string, motDePasse: string) => {
    definirIdentite(await seConnecterCognito(email, motDePasse));
  }, []);

  const deconnecter = useCallback(() => {
    seDeconnecterCognito();
    definirIdentite(null);
  }, []);

  const valeur = useMemo<ContexteAuth>(
    () => ({
      identite,
      chargement,
      seConnecter: connecter,
      seDeconnecter: deconnecter,
      definirIdentite,
    }),
    [identite, chargement, connecter, deconnecter],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useAuth(): ContexteAuth {
  const contexte = useContext(Contexte);
  if (!contexte) throw new Error("useAuth doit être utilisé à l'intérieur de <FournisseurAuth>.");
  return contexte;
}

/** Identité garantie non nulle : à n'utiliser que dans un écran protégé par la route. */
export function useIdentite(): Identite {
  const { identite } = useAuth();
  if (!identite) throw new Error("Aucun utilisateur connecté sur un écran qui en exige un.");
  return identite;
}
